<template>
  <div class="business-details" v-loading="loading.business">
    <el-row :gutter="18">
      <el-col :md="24" class="mb-3">
        <el-card class="box-card" :body-style="{ padding: '20px 20px 2px 20px' }">
          <div slot="header" class="clearfix detail-header">
            <i class="fa fa-building"></i>Business
            <el-button type="primary" icon="fa fa-pencil-alt" class="mr-3" @click="editBusiness"
              v-if="isAdmin">Edit</el-button>
          </div>
          <el-row :gutter="18" justify="start">
            <el-col :sm="12" :lg="6">
              <static-field label="Business Name" :value="business.businessName"></static-field>
            </el-col>
            <el-col :sm="12" :lg="6">
              <static-field label="Rep. Name" :value="business.repName"></static-field>
            </el-col>
            <el-col :sm="12" :lg="6">
              <static-field label="Rep. Driver's License" :value="business.stateId"></static-field>
            </el-col>
            <el-col :sm="12" :lg="6">
              <static-field label="Address" :value="address"></static-field>
            </el-col>
            <el-col :sm="12" :lg="6">
              <static-field label="Phone" :value="business.phone"></static-field>
            </el-col>
            <el-col :sm="12" :lg="6">
              <static-field label="Email" :value="business.email"></static-field>
            </el-col>
            <el-col :sm="12" :lg="6">
              <static-field label="Lead Source" :value="business.leadSource"></static-field>
            </el-col>
            <el-col :sm="12" :lg="6">
              <static-field label="Office" :value="office.efin"></static-field>
            </el-col>
          </el-row>
        </el-card>
      </el-col>
    </el-row>
    <el-row :gutter="18">
      <el-col :md="24" class="mb-3">
        <el-card class="box-card" :body-style="{ padding: '0' }" v-loading="loading.referrals">
          <div slot="header" class="clearfix detail-header referral">
            <i class="fa fa-retweet"></i> <span style="font-weight: normal;">RAF Code:</span> {{ business.rafCode }}
            <el-button type="primary" icon="fa fa-plus" @click="displayLogReferralDialog">Log Referral</el-button>
            <el-button class="mr-3" type="primary" icon="fa fa-tags" :disabled="!hasReferralsReadyForPayout"
              @click="navigateToPayReferrals">Pay Referrals</el-button>
            <raf-slips-pdf-button icon="fa fa-list-alt" :business-id="business.id">View RAF Slips</raf-slips-pdf-button>
          </div>
          <referral-table :referrals="referrals" @changeReferralOffice="onChangeReferralOffice"
            @overrideReferral="onOverrideReferral" @deleteReferral="onDeleteReferral" />
        </el-card>
      </el-col>
    </el-row>
    <el-dialog title="Log Referral" :visible.sync="logReferralDialogVm.visible" :show-close="false"
      :close-on-click-modal="false" :close-on-press-escape="false" width="50%">
      <log-referral-dialog :rafCode="logReferralDialogVm.rafCode" @close="logReferralDialogVm.visible = false"
        @addedReferral="onAddedReferral"></log-referral-dialog>
    </el-dialog>
    <el-dialog title="Edit Business" :visible.sync="editBusinessDialogVisible" :show-close="false"
      :close-on-click-modal="false" :close-on-press-escape="false" width="60%" v-if="isAdmin">
      <edit-business-dialog @close="editBusinessDialogVisible = false" :business="business"
        @businessUpdated="onBusinessUpdated"></edit-business-dialog>
    </el-dialog>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import findIndex from 'lodash/findIndex';
import get from 'lodash/get';
import api from '@/api';
import { Business, Referral } from '@/model';
import StaticField from '@/components/widgets/StaticField.vue';
import EditBusinessDialog from '@/components/dialog-content/EditBusiness.vue';
import { RAF_STATE } from '@/enum/RAFStatuses';
import RafSlipsPdfButton from '@/components/PDFButton/RAFSlips';
import ReferralTable from '@/components/tables/Referral.vue';
import LogReferralDialog from '@/components/dialog-content/LogReferral.vue';

export default {
  data() {
    return {
      loading: {
        business: false,
        referrals: false,
      },
      business: new Business(),
      referrals: [],
      editBusinessDialogVisible: false,
      logReferralDialogVm: {
        visible: false,
        rafCode: null,
      },
    };
  },
  watch: {
    '$route.params.businessId': {
      immediate: true,
      handler(businessId) {
        this.fetchBusiness(businessId);
        this.fetchBusinessReferrals(businessId);
      },
    },
  },
  methods: {
    async fetchBusiness(businessId) {
      try {
        this.loading.business = true;
        const response = await api.business.get(businessId);
        this.business = new Business(response.data);
      } catch (e) {
        console.warn('business could not be loaded', e);
        this.$notify({
          type: 'error',
          title: 'Error',
          message: 'Could not load business',
        });
      } finally {
        this.loading.business = false;
      }
    },
    async fetchBusinessReferrals(businessId) {
      try {
        this.loading.referrals = true;
        const response = await api.business.getReferrals(businessId);
        this.referrals = response.data.map(referral => new Referral(referral));
      } catch (e) {
        console.warn('business referrals could not be loaded', e);
        this.$notify({
          type: 'error',
          title: 'Error',
          message: 'Could not load business referrals',
        });
      } finally {
        this.loading.referrals = false;
      }
    },
    editBusiness() {
      this.editBusinessDialogVisible = true;
    },
    onBusinessUpdated(business) {
      this.business = new Business(business);
    },
    displayLogReferralDialog() {
      this.logReferralDialogVm.rafCode = this.business.rafCode;
      this.logReferralDialogVm.visible = true;
    },
    async onChangeReferralOffice(updateTO) {
      try {
        const response = await api.referral.changeOffice(updateTO.referral.id, updateTO.officeId);
        this.replaceReferral(new Referral(response.data));

        this.$notify.success({
          title: 'Success',
          message: 'The office for the referral has been updated.',
          position: 'bottom-right',
        });
      } catch (e) {
        this.$notify.error({
          title: 'Error',
          message: get(e, 'response.data', e.message),
          position: 'bottom-right',
        });
      }
    },
    async onOverrideReferral(fullReferral) {
      try {
        const response = await api.referral.override(fullReferral);
        this.replaceReferral(new Referral(response.data));

        this.$notify.success({
          title: 'Success',
          message: 'The status and amount for the referral have been updated.',
          position: 'bottom-right',
        });
      } catch (e) {
        this.$notify.error({
          title: 'Error',
          message: get(e, 'response.data', e.message),
          position: 'bottom-right',
        });
      }
    },
    async onDeleteReferral(referral) {
      try {
        await api.referral.remove(referral.id);
        const index = findIndex(this.referrals, { id: referral.id });
        this.referrals.splice(index, 1);

        this.$notify.success({
          title: 'Success',
          message: 'The referral has been deleted.',
          position: 'bottom-right',
        });
      } catch (e) {
        this.$notify.error({
          title: 'Error',
          message: get(e, 'response.data', e.message),
          position: 'bottom-right',
        });
      }
    },
    onAddedReferral(referral) {
      this.referrals.push(referral);
    },
    replaceReferral(referral) {
      const index = findIndex(this.referrals, { id: referral.id });
      this.referrals.splice(index, 1, referral);
    },
    navigateToPayReferrals() {
      this.$router.push({
        name: 'payBusinessReferral',
        params: {
          entityId: this.business.id,
        },
      });
    },
  },
  computed: {
    ...mapGetters('User', ['isAdmin']),
    ...mapGetters('Lookup', ['officeMap']),
    address() {
      if (this.business) {
        const address = this.business.address || '';
        const city = this.business.city || '';
        const state = this.business.state || '';
        const zip = this.business.zip || '';

        let addressString = '';
        if (address.length) {
          addressString += `${address}\n`;
        }
        if (city.length) {
          addressString += city + (state.length ? ', ' : '');
        }

        addressString += `${state} ${zip}`;
        return addressString;
      }
      return '';
    },
    hasReferralsReadyForPayout() {
      const referrals = this.referrals;
      return referrals.findIndex(referral => referral.status === RAF_STATE.APPROVED && !referral.transactionId) > -1;
    },
    office() {
      if (this.business.officeId !== undefined) {
        return this.officeMap[this.business.officeId];
      }
      return {};
    },
  },
  components: {
    StaticField,
    EditBusinessDialog,
    RafSlipsPdfButton,
    ReferralTable,
    LogReferralDialog,
  },
};

</script>

<style lang="scss" scoped>
.detail-header {
  font-size: 18px;
  font-weight: bold;
  line-height: 24px;

  button {
    float: right;
    padding: 5px 5px;
  }
}
</style>
